import { useTranslation } from "react-i18next";
import "@/components/StarRating/StarRating.scss";

interface StarRatingProps {
  /** 0 to 5; fractions are drawn as partly filled stars (e.g. 4.6). */
  value: number;
  size?: "small" | "medium" | "large";
}

const STAR_PATH =
  "M12 2.5l2.93 5.94 6.56.95-4.75 4.63 1.12 6.53L12 17.47l-5.86 3.08 1.12-6.53L2.5 9.39l6.56-.95z";

/** Read-only row of five stars, exposed to assistive tech as a single "Rated x out of 5". */
function StarRating({ value, size = "medium" }: StarRatingProps) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(5, value));

  return (
    <span
      className={`star-rating star-rating--${size}`}
      role="img"
      aria-label={t("reviews.ratedOutOfFive", {
        rating: clamped.toFixed(1).replace(/\.0$/, ""),
      })}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fill = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="star-rating__star">
            <svg
              viewBox="0 0 24 24"
              className="star-rating__empty"
              aria-hidden="true"
            >
              <path d={STAR_PATH} />
            </svg>
            <span
              className="star-rating__fill"
              style={{ width: `${fill * 100}%` }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={STAR_PATH} />
              </svg>
            </span>
          </span>
        );
      })}
    </span>
  );
}

export default StarRating;
export { STAR_PATH };
export type { StarRatingProps };
